import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from "recharts";
import { getAuthToken } from "./api";
import "./PilotAnalytics.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const REFRESH_MS = 30_000;

function fmtMs(ms) {
  if (!ms) return "—";
  const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}
function fmtUptime(s) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
function usd(v) { return v == null ? "—" : `$${Number(v).toFixed(4)}`; }
function pct(v) { return v == null ? "—" : `${v}%`; }

function StatCard({ label, value, color }) {
  return (
    <div className="pa-stat-card" style={{ borderColor: color ? `${color}33` : undefined }}>
      <div className="pa-stat-value" style={{ color: color || "#a78bfa" }}>{value ?? "—"}</div>
      <div className="pa-stat-label">{label}</div>
    </div>
  );
}

function SectionHdr({ title, icon }) {
  return (
    <div className="pa-section-header">
      <span className="pa-section-icon">{icon}</span>
      <h2 className="pa-section-title">{title}</h2>
    </div>
  );
}

function Alerts({ alerts }) {
  if (!alerts || alerts.length === 0) {
    return <div className="pa-alert pa-alert-ok"><span>✓</span> All systems nominal</div>;
  }
  return (
    <div className="pa-alerts-list">
      {alerts.map((a, i) => (
        <div key={i} className={`pa-alert pa-alert-${a.level}`}>
          <span>{a.level === "error" ? "✕" : "⚠"}</span> {a.msg}
        </div>
      ))}
    </div>
  );
}

export default function PilotAnalytics() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [secsAgo, setSecsAgo]   = useState(0);
  const tickRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_BASE}/api/admin/pilot/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `Error ${res.status}`);
      }
      setData(await res.json());
      setSecsAgo(0);
      setError(null);
    } catch (e) {
      setError(e.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, REFRESH_MS); return () => clearInterval(t); }, [load]);
  useEffect(() => { tickRef.current = setInterval(() => setSecsAgo(s => s + 1), 1000); return () => clearInterval(tickRef.current); }, []);

  if (loading) return <div className="pa-container"><div className="pa-loading">Loading analytics…</div></div>;
  if (error)   return (
    <div className="pa-container">
      <div className="pa-error"><h3>Unable to load pilot analytics</h3><p>{error}</p>
        <button onClick={load} className="pa-btn-refresh">Retry</button></div>
    </div>
  );

  const { live_monitoring: lm, infrastructure: inf, costs, campaign_analytics, bottlenecks, alerts, logs } = data;

  const epChart = (bottlenecks.slowest_endpoints || [])
    .filter(e => e.count > 0).slice(0, 8)
    .map(e => ({
      name: (e.key || "").split(" ").slice(-1)[0].replace(/\/api\//, "/").slice(-26),
      p95: e.p95_ms, avg: e.avg_ms,
    }));

  const fsChart = Object.entries(inf.fs_writes_by_col || {})
    .map(([k, v]) => ({ name: k.replace(/_/g, " "), writes: v }));

  return (
    <div className="pa-container">
      {/* Header */}
      <div className="pa-header">
        <div>
          <h1 className="pa-title">Pilot Analytics</h1>
          <p className="pa-subtitle">SJCE Campus Drive · Infrastructure &amp; Cost Monitor</p>
        </div>
        <div className="pa-header-right">
          <span className="pa-meta">Uptime {fmtUptime(inf.uptime_s)}</span>
          <span className="pa-meta">Updated {secsAgo}s ago</span>
          <button className="pa-btn-refresh" onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {/* Alerts */}
      <Alerts alerts={alerts} />

      {/* 1. Live Monitoring */}
      <div className="pa-section">
        <SectionHdr title="Live Assessment Monitoring" icon="🟢" />
        <div className="pa-grid4">
          <StatCard label="Active Candidates"        value={lm.active_candidates}   color="#36c8a0" />
          <StatCard label="Total Started"            value={lm.total_started}       color="#a78bfa" />
          <StatCard label="Total Completed"          value={lm.total_completed}     color="#36c8a0" />
          <StatCard label="Completion Rate"          value={pct(lm.completion_pct)} color={lm.completion_pct >= 70 ? "#36c8a0" : "#f6c05a"} />
          <StatCard label="MCQ Submissions"          value={lm.mcq_submissions}     color="#a78bfa" />
          <StatCard label="Coding Submissions"       value={lm.coding_submissions}  color="#60d9f7" />
          <StatCard label="Avg Assessment Duration"  value={fmtMs(lm.avg_duration_ms)} color="#f6c05a" />
          <StatCard label="API Requests / min"       value={inf.rpm}                color="#a78bfa" />
        </div>
      </div>

      {/* 2. Infrastructure */}
      <div className="pa-section">
        <SectionHdr title="Infrastructure Metrics" icon="⚙️" />
        <div className="pa-grid4">
          <StatCard label="Total API Requests"       value={inf.total_requests}                                                               color="#a78bfa" />
          <StatCard label="Avg Response"             value={`${inf.avg_response_ms} ms`} color={inf.avg_response_ms > 2000 ? "#f87171" : "#36c8a0"} />
          <StatCard label="P95 Response"             value={`${inf.p95_response_ms} ms`} color={inf.p95_response_ms > 5000 ? "#f87171" : "#f6c05a"} />
          <StatCard label="Error Rate"               value={pct(inf.error_rate_pct)}      color={inf.error_rate_pct > 2 ? "#f87171" : "#36c8a0"} />
          <StatCard label="MCQ Submit Requests"      value={inf.assess_submits}            color="#a78bfa" />
          <StatCard label="Coding Submit Requests"   value={inf.coding_submits}            color="#60d9f7" />
          <StatCard label="Judge0 Executions"        value={inf.judge0_calls}              color="#f6c05a" />
          <StatCard label="Error Count"              value={inf.error_count}               color={inf.error_count > 0 ? "#f87171" : "#36c8a0"} />
        </div>

        <div className="pa-charts-row">
          {epChart.length > 0 && (
            <div className="pa-chart-card">
              <div className="pa-chart-title">Slowest Endpoints — P95 ms</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={epChart} layout="vertical" margin={{left:8,right:20,top:4,bottom:4}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" horizontal={false} />
                  <XAxis type="number" tick={{fontSize:10,fill:"rgba(255,255,255,0.4)"}} tickLine={false} />
                  <YAxis dataKey="name" type="category" width={110} tick={{fontSize:9,fill:"rgba(255,255,255,0.5)"}} tickLine={false} />
                  <Tooltip contentStyle={{background:"#1e2235",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8}} labelStyle={{color:"rgba(255,255,255,0.7)",fontSize:11}} />
                  <Bar dataKey="p95" name="P95 ms" radius={[0,3,3,0]}>
                    {epChart.map((e, i) => (
                      <Cell key={i} fill={e.p95 > 5000 ? "#f87171" : e.p95 > 2000 ? "#f6c05a" : "#a78bfa"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {fsChart.length > 0 && (
            <div className="pa-chart-card">
              <div className="pa-chart-title">Firestore Writes by Collection</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={fsChart} margin={{left:8,right:20,top:4,bottom:36}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                  <XAxis dataKey="name" tick={{fontSize:9,fill:"rgba(255,255,255,0.45)"}} tickLine={false} angle={-20} textAnchor="end" interval={0} />
                  <YAxis tick={{fontSize:10,fill:"rgba(255,255,255,0.45)"}} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{background:"#1e2235",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8}} />
                  <Bar dataKey="writes" name="Writes" fill="#36c8a0" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="pa-kv-row">
          <div className="pa-kv"><span>Est. Firestore Reads</span><strong>{inf.fs_reads_est.toLocaleString()}</strong></div>
          <div className="pa-kv"><span>Est. Firestore Writes</span><strong>{inf.fs_writes_est.toLocaleString()}</strong></div>
        </div>
      </div>

      {/* 3. Costs */}
      <div className="pa-section">
        <SectionHdr title="Cost Estimation" icon="💰" />
        <div className="pa-cost-grid">
          <div className="pa-cost-card">
            <div className="pa-cost-card-title">API / Compute</div>
            <div className="pa-kv"><span>Total Requests</span><strong>{costs.api_requests.toLocaleString()}</strong></div>
            <div className="pa-kv"><span>Server (fixed)</span><strong>${costs.compute_monthly_usd}/mo</strong></div>
          </div>
          <div className="pa-cost-card">
            <div className="pa-cost-card-title">Firebase</div>
            <div className="pa-kv"><span>Reads (est.)</span><strong>{costs.fs_reads.toLocaleString()}</strong></div>
            <div className="pa-kv"><span>Writes (est.)</span><strong>{costs.fs_writes.toLocaleString()}</strong></div>
            <div className="pa-kv"><span>Cost</span><strong className="pa-cost-amt">{usd(costs.fs_usd)}</strong></div>
          </div>
          <div className="pa-cost-card">
            <div className="pa-cost-card-title">Judge0</div>
            <div className="pa-kv"><span>Executions</span><strong>{costs.judge0_calls.toLocaleString()}</strong></div>
            <div className="pa-kv"><span>VPS (fixed)</span><strong>${costs.judge0_monthly_usd}/mo</strong></div>
            <div className="pa-kv"><span>Variable cost</span><strong className="pa-cost-amt">{usd(costs.judge0_usd)}</strong></div>
          </div>
          <div className="pa-cost-card">
            <div className="pa-cost-card-title">LLM (Groq)</div>
            <div className="pa-kv"><span>Question Gens</span><strong>{costs.llm_question_gens}</strong></div>
            <div className="pa-kv"><span>Interview Evals</span><strong>{costs.llm_iv_evals}</strong></div>
            <div className="pa-kv"><span>Coding Evals</span><strong>{costs.llm_coding_evals}</strong></div>
            <div className="pa-kv"><span>Cost</span><strong className="pa-cost-amt">{usd(costs.llm_usd)}</strong></div>
          </div>
        </div>
        <div className="pa-cost-summary">
          <div className="pa-cost-total"><span>Total Variable Cost</span><strong className="pa-big-num">{usd(costs.total_usd)}</strong></div>
          <div className="pa-cost-total"><span>Cost / Candidate</span><strong>{usd(costs.cost_per_candidate)}</strong></div>
          <div className="pa-cost-total"><span>Cost / Completion</span><strong>{usd(costs.cost_per_completion)}</strong></div>
        </div>
      </div>

      {/* 4. Campaign Analytics */}
      <div className="pa-section">
        <SectionHdr title="Campaign Analytics" icon="📊" />
        {campaign_analytics.length === 0 ? <p className="pa-empty">No campaigns yet.</p> : (
          <div className="pa-table-wrap">
            <table className="pa-table">
              <thead>
                <tr>
                  <th>Campaign</th><th>Type</th><th>Status</th>
                  <th>Started</th><th>Completed</th><th>Rate</th>
                  <th>Avg%</th><th>Tech%</th><th>Apt%</th><th>Work%</th><th>Coding</th>
                </tr>
              </thead>
              <tbody>
                {campaign_analytics.map(c => (
                  <tr key={c.id}>
                    <td><div className="pa-cam-title">{c.title}</div><div className="pa-cam-date">{c.created_at}</div></td>
                    <td><span className="pa-badge pa-badge-type">{c.event_type}</span></td>
                    <td><span className={`pa-badge ${c.status === "ACTIVE" ? "pa-badge-active" : "pa-badge-inactive"}`}>{c.status}</span></td>
                    <td>{c.started}</td>
                    <td>{c.completed}</td>
                    <td><span className={c.completion_rate >= 70 ? "pa-good" : c.completion_rate >= 40 ? "pa-warn" : "pa-bad"}>{pct(c.completion_rate)}</span></td>
                    <td>{c.avg_score != null ? `${c.avg_score}%` : "—"}</td>
                    <td>{c.avg_technical != null ? `${c.avg_technical}%` : "—"}</td>
                    <td>{c.avg_aptitude != null ? `${c.avg_aptitude}%` : "—"}</td>
                    <td>{c.avg_workstyle != null ? `${c.avg_workstyle}%` : "—"}</td>
                    <td>{c.coding_submissions || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 5. Bottlenecks */}
      <div className="pa-section">
        <SectionHdr title="Bottleneck Monitoring" icon="🔍" />
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead>
              <tr><th>Endpoint</th><th>Requests</th><th>Avg ms</th><th>P95 ms</th><th>Errors</th></tr>
            </thead>
            <tbody>
              {(bottlenecks.slowest_endpoints || []).map((ep, i) => (
                <tr key={i}>
                  <td className="pa-ep-name">{ep.key}</td>
                  <td>{ep.count}</td>
                  <td><span className={ep.avg_ms > 2000 ? "pa-warn" : ""}>{ep.avg_ms}</span></td>
                  <td><span className={ep.p95_ms > 5000 ? "pa-bad" : ep.p95_ms > 2000 ? "pa-warn" : "pa-good"}>{ep.p95_ms}</span></td>
                  <td><span className={ep.errors > 0 ? "pa-bad" : "pa-good"}>{ep.errors}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pa-kv-row" style={{marginTop:"1rem"}}>
          <div className="pa-kv"><span>Judge0 Total Executions</span><strong>{bottlenecks.judge0_calls}</strong></div>
          <div className="pa-kv"><span>Failed Submissions</span><strong className={bottlenecks.total_errors > 0 ? "pa-bad" : "pa-good"}>{bottlenecks.total_errors}</strong></div>
        </div>
      </div>

      {/* 6. Alerts detail */}
      <div className="pa-section">
        <SectionHdr title="Alert Status" icon="🚨" />
        <Alerts alerts={alerts} />
        <div className="pa-kv-row" style={{marginTop:"1rem"}}>
          <div className="pa-kv"><span>API P95 &gt; 5s</span><strong className={inf.p95_response_ms > 5000 ? "pa-bad" : "pa-good"}>{inf.p95_response_ms > 5000 ? "TRIGGERED" : "OK"}</strong></div>
          <div className="pa-kv"><span>Error rate &gt; 2%</span><strong className={inf.error_rate_pct > 2 ? "pa-bad" : "pa-good"}>{inf.error_rate_pct > 2 ? "TRIGGERED" : "OK"}</strong></div>
          <div className="pa-kv"><span>Judge0 &gt; 500 calls</span><strong className={bottlenecks.judge0_calls > 500 ? "pa-warn" : "pa-good"}>{bottlenecks.judge0_calls > 500 ? "WARNING" : "OK"}</strong></div>
          <div className="pa-kv"><span>LLM &gt; 50 gens</span><strong className={costs.llm_question_gens > 50 ? "pa-warn" : "pa-good"}>{costs.llm_question_gens > 50 ? "WARNING" : "OK"}</strong></div>
        </div>
      </div>

      {/* 7. Logs */}
      <div className="pa-section">
        <SectionHdr title="Recent Request Logs" icon="📋" />
        <div className="pa-table-wrap pa-logs-wrap">
          <table className="pa-table">
            <thead>
              <tr><th>Time</th><th>Method</th><th>Endpoint</th><th>Status</th><th>ms</th><th>Campaign</th><th>Candidate</th></tr>
            </thead>
            <tbody>
              {(logs || []).map((l, i) => (
                <tr key={i} className={l.error ? "pa-row-err" : ""}>
                  <td className="pa-log-time">{new Date(l.timestamp * 1000).toLocaleTimeString()}</td>
                  <td><span className="pa-method">{l.method}</span></td>
                  <td className="pa-ep-name">{l.endpoint}</td>
                  <td><span className={l.status_code >= 400 ? "pa-bad" : "pa-good"}>{l.status_code}</span></td>
                  <td><span className={l.duration_ms > 3000 ? "pa-warn" : ""}>{l.duration_ms}</span></td>
                  <td className="pa-log-meta">{l.campaign_id || "—"}</td>
                  <td className="pa-log-meta">{l.candidate_hash || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pa-footer">
        Dashboard query took {data.query_ms} ms · Auto-refreshes every 30s · Counters reset on server restart
      </div>
    </div>
  );
}
